// src/phone.ts
import parsePhoneNumberFromString from "libphonenumber-js";
var DEFAULT_COUNTRY = "RU";
var RU_DIGITS_LENGTH = 11;
function onlyDigits(value) {
  return value.replace(/\D/g, "");
}
function normalizeRuDigits(digits) {
  if (digits.length === 0) return "";
  if (digits.startsWith("8")) return `7${digits.slice(1)}`;
  if (digits.startsWith("9") && digits.length <= 10) return `7${digits}`;
  return digits;
}
function formatRuPhone(rawDigits) {
  const digits = normalizeRuDigits(onlyDigits(rawDigits)).slice(0, RU_DIGITS_LENGTH);
  if (digits.length === 0) return "";
  const country = digits.slice(0, 1);
  const area = digits.slice(1, 4);
  const part1 = digits.slice(4, 7);
  const part2 = digits.slice(7, 9);
  const part3 = digits.slice(9, 11);
  let result = `+${country}`;
  if (area) result += ` (${area}`;
  if (area.length === 3) result += ")";
  if (part1) result += ` ${part1}`;
  if (part2) result += `-${part2}`;
  if (part3) result += `-${part3}`;
  return result;
}
function normalizePhone(input, country = DEFAULT_COUNTRY) {
  const raw = (input ?? "").trim();
  if (raw.length === 0) return null;
  const digits = onlyDigits(raw);
  if (digits.length === 0) return null;
  const candidate = raw.startsWith("+") ? raw : `+${normalizeRuDigits(digits)}`;
  const parsed = parsePhoneNumberFromString(candidate, country);
  if (!parsed || !parsed.isValid()) return null;
  return parsed.number;
}
function isValidPhone(input, country = DEFAULT_COUNTRY) {
  return normalizePhone(input, country) !== null;
}
function toOnecPhone(e164) {
  return onlyDigits(e164);
}
function formatPhoneForDisplay(e164) {
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed?.country === "RU") return formatRuPhone(parsed.number);
  return parsed ? parsed.formatInternational() : e164;
}
function maskPhone(e164) {
  const digits = onlyDigits(e164);
  if (digits.length < 4) return "***";
  return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ***-**-${digits.slice(-2)}`;
}

// src/format.ts
var RUB = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});
function formatMoney(amountMinor) {
  return RUB.format(amountMinor / 100);
}
function rublesToMinor(rubles) {
  return Math.round(Number(rubles) * 100);
}
function minorToRubles(amountMinor) {
  return amountMinor / 100;
}
function minorToYookassaValue(amountMinor) {
  return (amountMinor / 100).toFixed(2);
}
var DATE_FMT = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
var DATE_SHORT_FMT = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
var DATE_TIME_FMT = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit"
});
function formatDate(value) {
  return DATE_FMT.format(toDate(value));
}
function formatDateShort(value) {
  return DATE_SHORT_FMT.format(toDate(value));
}
function formatDateTime(value) {
  return DATE_TIME_FMT.format(toDate(value));
}
function toOnecDate(value) {
  const date = toDate(value);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}
function toDate(value) {
  return value instanceof Date ? value : new Date(value);
}
function plural(count, forms) {
  const abs = Math.abs(count) % 100;
  const tail = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (tail > 1 && tail < 5) return forms[1];
  if (tail === 1) return forms[0];
  return forms[2];
}
function formatDaysLeft(days) {
  return `${days} ${plural(days, ["\u0434\u0435\u043D\u044C", "\u0434\u043D\u044F", "\u0434\u043D\u0435\u0439"])}`;
}
function formatMonths(months) {
  return `${months} ${plural(months, ["\u043C\u0435\u0441\u044F\u0446", "\u043C\u0435\u0441\u044F\u0446\u0430", "\u043C\u0435\u0441\u044F\u0446\u0435\u0432"])}`;
}
function daysBetween(from, to) {
  const ms = toDate(to).getTime() - toDate(from).getTime();
  return Math.max(0, Math.ceil(ms / 864e5));
}
function addMonthsSafe(date, months) {
  const result = new Date(toDate(date));
  const targetDay = result.getDate();
  result.setMonth(result.getMonth() + months);
  if (result.getDate() < targetDay) {
    result.setDate(0);
  }
  return result;
}
function formatCardMask(last4) {
  return `\u2022\u2022\u2022\u2022 ${last4}`;
}

// src/validation.ts
import { z } from "zod";
var phoneSchema = z.string({ required_error: "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043D\u043E\u043C\u0435\u0440 \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0430" }).trim().min(1, "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u043D\u043E\u043C\u0435\u0440 \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0430").refine((value) => isValidPhone(value), "\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u043D\u043E\u043C\u0435\u0440 \u0442\u0435\u043B\u0435\u0444\u043E\u043D\u0430").transform((value) => normalizePhone(value));
var OTP_MIN_LENGTH = 4;
var OTP_MAX_LENGTH = 6;
var otpCodeSchema = z.string({ required_error: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043A\u043E\u0434 \u0438\u0437 SMS" }).trim().regex(new RegExp(`^\\d{${OTP_MIN_LENGTH},${OTP_MAX_LENGTH}}$`), "\u041A\u043E\u0434 \u0441\u043E\u0441\u0442\u043E\u0438\u0442 \u0442\u043E\u043B\u044C\u043A\u043E \u0438\u0437 \u0446\u0438\u0444\u0440");
var emailSchema = z.string({ required_error: "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u044D\u043B\u0435\u043A\u0442\u0440\u043E\u043D\u043D\u0443\u044E \u043F\u043E\u0447\u0442\u0443" }).trim().toLowerCase().min(1, "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u044D\u043B\u0435\u043A\u0442\u0440\u043E\u043D\u043D\u0443\u044E \u043F\u043E\u0447\u0442\u0443").email("\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0430\u0434\u0440\u0435\u0441 \u044D\u043B\u0435\u043A\u0442\u0440\u043E\u043D\u043D\u043E\u0439 \u043F\u043E\u0447\u0442\u044B").max(254, "\u0410\u0434\u0440\u0435\u0441 \u0441\u043B\u0438\u0448\u043A\u043E\u043C \u0434\u043B\u0438\u043D\u043D\u044B\u0439");
var nameRegex = /^[A-Za-zА-Яа-яЁё]+(?:[ '\-][A-Za-zА-Яа-яЁё]+)*$/;
var nameSchema = z.string().trim().min(2, "\u041C\u0438\u043D\u0438\u043C\u0443\u043C 2 \u0441\u0438\u043C\u0432\u043E\u043B\u0430").max(60, "\u041C\u0430\u043A\u0441\u0438\u043C\u0443\u043C 60 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432").regex(nameRegex, "\u0414\u043E\u043F\u0443\u0441\u0442\u0438\u043C\u044B \u0442\u043E\u043B\u044C\u043A\u043E \u0431\u0443\u043A\u0432\u044B, \u0434\u0435\u0444\u0438\u0441 \u0438 \u0430\u043F\u043E\u0441\u0442\u0440\u043E\u0444");
var optionalNameSchema = z.union([nameSchema, z.literal("")]).optional().transform((value) => value === "" ? void 0 : value);
var birthdaySchema = z.string().trim().regex(/^\d{2}\.\d{2}\.\d{4}$/, "\u0424\u043E\u0440\u043C\u0430\u0442 \u0434\u0430\u0442\u044B: \u0414\u0414.\u041C\u041C.\u0413\u0413\u0413\u0413").refine((value) => parseRuDate(value) !== null, "\u0422\u0430\u043A\u043E\u0439 \u0434\u0430\u0442\u044B \u043D\u0435 \u0441\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u0435\u0442").refine((value) => {
  const date = parseRuDate(value);
  return date !== null && date.getTime() <= Date.now();
}, "\u0414\u0430\u0442\u0430 \u0440\u043E\u0436\u0434\u0435\u043D\u0438\u044F \u043D\u0435 \u043C\u043E\u0436\u0435\u0442 \u0431\u044B\u0442\u044C \u0432 \u0431\u0443\u0434\u0443\u0449\u0435\u043C").refine((value) => {
  const date = parseRuDate(value);
  return date !== null && yearsSince(date) <= 120;
}, "\u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0434\u0430\u0442\u0443 \u0440\u043E\u0436\u0434\u0435\u043D\u0438\u044F");
var requiredConsentSchema = z.literal(true, {
  errorMap: () => ({ message: "\u0411\u0435\u0437 \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u044F \u043F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C \u043D\u0435\u043B\u044C\u0437\u044F" })
});
var optionalConsentSchema = z.boolean().default(false);
var requestOtpSchema = z.object({
  phone: phoneSchema,
  /** Согласие на обработку персональных данных — обязательное. */
  consentPersonalData: requiredConsentSchema,
  /** Рекламные рассылки — строго отдельно и необязательно. */
  consentMarketing: optionalConsentSchema,
  captchaToken: z.string().optional()
});
var verifyOtpSchema = z.object({
  phone: phoneSchema,
  code: otpCodeSchema
});
var consentStatusSchema = z.object({
  phone: phoneSchema
});
var updateProfileSchema = z.object({
  lastName: optionalNameSchema,
  firstName: nameSchema,
  secondName: optionalNameSchema,
  email: emailSchema,
  birthday: birthdaySchema.optional()
});
var createSubscriptionSchema = z.object({
  tariffId: z.string().uuid("\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0442\u0430\u0440\u0438\u0444"),
  email: emailSchema,
  consentOffer: requiredConsentSchema,
  consentRecurrent: z.boolean().default(false),
  isRecurrentTariff: z.boolean().default(false),
  savePaymentMethod: z.boolean().default(true)
}).refine((data) => !data.isRecurrentTariff || data.consentRecurrent === true, {
  path: ["consentRecurrent"],
  message: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u0435 \u0441\u043E\u0433\u043B\u0430\u0441\u0438\u0435 \u043D\u0430 \u043F\u0435\u0440\u0438\u043E\u0434\u0438\u0447\u0435\u0441\u043A\u0438\u0435 \u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044F"
});
var cancelSubscriptionSchema = z.object({
  subscriptionId: z.string().uuid(),
  reasonId: z.string().optional(),
  comment: z.string().trim().max(500, "\u041C\u0430\u043A\u0441\u0438\u043C\u0443\u043C 500 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432").optional()
});
var adminLoginSchema = z.object({
  email: emailSchema,
  password: z.string().min(8, "\u041C\u0438\u043D\u0438\u043C\u0443\u043C 8 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432"),
  totp: z.string().regex(/^\d{6}$/, "\u041A\u043E\u0434 \u0438\u0437 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F \u0441\u043E\u0441\u0442\u043E\u0438\u0442 \u0438\u0437 6 \u0446\u0438\u0444\u0440")
});
var refundSchema = z.object({
  paymentId: z.string().uuid(),
  amountMinor: z.number().int().positive("\u0421\u0443\u043C\u043C\u0430 \u0434\u043E\u043B\u0436\u043D\u0430 \u0431\u044B\u0442\u044C \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0443\u043B\u044F"),
  reason: z.string().trim().min(5, "\u041E\u043F\u0438\u0448\u0438\u0442\u0435 \u043F\u0440\u0438\u0447\u0438\u043D\u0443 \u0432\u043E\u0437\u0432\u0440\u0430\u0442\u0430")
});
function parseRuDate(value) {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}
function yearsSince(date) {
  const now = /* @__PURE__ */ new Date();
  let years = now.getFullYear() - date.getFullYear();
  const monthDiff = now.getMonth() - date.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && now.getDate() < date.getDate()) years -= 1;
  return years;
}

// src/onec-errors.ts
var OnecErrorCode = {
  CLIENT_NOT_FOUND: 1010,
  TICKET_NOT_FOUND: 1014,
  /** Платёж уже записан в 1С — повторная отправка того же платежа. Трактуем как успех. */
  PAYMENT_ALREADY_REGISTERED: 1018,
  ITEM_NOT_FOUND: 1020,
  PAYMENT_ERROR: 1022,
  CLUB_NOT_FOUND: 1053,
  INVALID_PARAMETERS: 1049,
  /** Введён неверный код подтверждения. */
  CONFIRMATION_CODE_INVALID: 1065,
  /** Код подтверждения просрочен или уже использован. */
  CONFIRMATION_CODE_EXPIRED: 1068,
  EMAIL_REQUIRED_FOR_PURCHASE: 2013,
  PHONE_ALREADY_EXISTS: 2016,
  INVALID_JSON: 2020,
  ACCESS_DENIED: 2050,
  PHONE_REQUIRED: 4e3,
  INVALID_PASS_TOKEN: 4002,
  INVALID_CREDENTIALS: 4003,
  USER_TOKEN_MISSING: 4004,
  USER_TOKEN_INVALID: 4005,
  CLIENT_ALREADY_EXISTS: 4009,
  REGISTRATION_INCOMPLETE: 4013,
  CLIENT_NOT_REGISTERED: 4027,
  PHONE_NOT_FOUND: 4028,
  ITEM_NO_LONGER_AVAILABLE: 4034,
  INVALID_SIGNATURE: 4059
};
function isUserTokenError(code) {
  return code === OnecErrorCode.USER_TOKEN_MISSING || code === OnecErrorCode.USER_TOKEN_INVALID;
}
function isConfirmationCodeError(code) {
  return code === OnecErrorCode.CONFIRMATION_CODE_INVALID || code === OnecErrorCode.CONFIRMATION_CODE_EXPIRED;
}
function isCredentialsRejectedError(code) {
  return code === OnecErrorCode.INVALID_CREDENTIALS || code === OnecErrorCode.REGISTRATION_INCOMPLETE || code === OnecErrorCode.ACCESS_DENIED;
}
function isClientMissingError(code) {
  return code === OnecErrorCode.CLIENT_NOT_FOUND || code === OnecErrorCode.CLIENT_NOT_REGISTERED || code === OnecErrorCode.PHONE_NOT_FOUND;
}
var USER_MESSAGES = {
  [OnecErrorCode.CLIENT_NOT_FOUND]: "\u041D\u0435 \u043D\u0430\u0448\u043B\u0438 \u0432\u0430\u0441 \u0432 \u0431\u0430\u0437\u0435 \u043A\u043B\u0443\u0431\u0430. \u041F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u0435 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044E.",
  [OnecErrorCode.TICKET_NOT_FOUND]: "\u0410\u0431\u043E\u043D\u0435\u043C\u0435\u043D\u0442 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D. \u041E\u0431\u043D\u043E\u0432\u0438\u0442\u0435 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0443 \u0438\u043B\u0438 \u043E\u0431\u0440\u0430\u0442\u0438\u0442\u0435\u0441\u044C \u0432 \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0443.",
  [OnecErrorCode.ITEM_NOT_FOUND]: "\u0422\u0430\u0440\u0438\u0444 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435 \u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D. \u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0434\u0440\u0443\u0433\u043E\u0439.",
  [OnecErrorCode.ITEM_NO_LONGER_AVAILABLE]: "\u0422\u0430\u0440\u0438\u0444 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435 \u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D. \u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0434\u0440\u0443\u0433\u043E\u0439.",
  [OnecErrorCode.EMAIL_REQUIRED_FOR_PURCHASE]: "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u044D\u043B\u0435\u043A\u0442\u0440\u043E\u043D\u043D\u0443\u044E \u043F\u043E\u0447\u0442\u0443 \u2014 \u043E\u043D\u0430 \u043D\u0443\u0436\u043D\u0430 \u0434\u043B\u044F \u0447\u0435\u043A\u0430.",
  [OnecErrorCode.PHONE_ALREADY_EXISTS]: "\u042D\u0442\u043E\u0442 \u043D\u043E\u043C\u0435\u0440 \u0443\u0436\u0435 \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D. \u0412\u043E\u0439\u0434\u0438\u0442\u0435 \u043F\u043E \u043A\u043E\u0434\u0443 \u0438\u0437 SMS.",
  [OnecErrorCode.PAYMENT_ERROR]: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043F\u0440\u043E\u0432\u0435\u0441\u0442\u0438 \u043E\u043F\u043B\u0430\u0442\u0443. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u0435\u0449\u0451 \u0440\u0430\u0437 \u0438\u043B\u0438 \u043E\u0431\u0440\u0430\u0442\u0438\u0442\u0435\u0441\u044C \u0432 \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0443.",
  // Клуб отключил свободную регистрацию: 1С отказывается отправлять код
  // на номер, которого нет в её базе.
  [OnecErrorCode.PHONE_NOT_FOUND]: "\u042D\u0442\u043E\u0442 \u043D\u043E\u043C\u0435\u0440 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D \u0432 \u0431\u0430\u0437\u0435 \u043A\u043B\u0443\u0431\u0430. \u041E\u0444\u043E\u0440\u043C\u0438\u0442\u0435 \u043A\u0430\u0440\u0442\u0443 \u043A\u043B\u0438\u0435\u043D\u0442\u0430 \u043D\u0430 \u0440\u0435\u0441\u0435\u043F\u0448\u0435\u043D\u0435 \u2014 \u043F\u043E\u0441\u043B\u0435 \u044D\u0442\u043E\u0433\u043E \u0432\u0445\u043E\u0434 \u0437\u0430\u0440\u0430\u0431\u043E\u0442\u0430\u0435\u0442.",
  [OnecErrorCode.CLIENT_NOT_REGISTERED]: "\u042D\u0442\u043E\u0442 \u043D\u043E\u043C\u0435\u0440 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D \u0432 \u0431\u0430\u0437\u0435 \u043A\u043B\u0443\u0431\u0430. \u041E\u0444\u043E\u0440\u043C\u0438\u0442\u0435 \u043A\u0430\u0440\u0442\u0443 \u043A\u043B\u0438\u0435\u043D\u0442\u0430 \u043D\u0430 \u0440\u0435\u0441\u0435\u043F\u0448\u0435\u043D\u0435 \u2014 \u043F\u043E\u0441\u043B\u0435 \u044D\u0442\u043E\u0433\u043E \u0432\u0445\u043E\u0434 \u0437\u0430\u0440\u0430\u0431\u043E\u0442\u0430\u0435\u0442."
};
function isRegistrationClosedError(code) {
  return code === OnecErrorCode.PHONE_NOT_FOUND || code === OnecErrorCode.CLIENT_NOT_REGISTERED;
}
function getUserMessage(code, fallback = "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0432\u044B\u043F\u043E\u043B\u043D\u0438\u0442\u044C \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u044E. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u043F\u043E\u0437\u0436\u0435.") {
  return USER_MESSAGES[code] ?? fallback;
}
export {
  DEFAULT_COUNTRY,
  OTP_MAX_LENGTH,
  OTP_MIN_LENGTH,
  OnecErrorCode,
  addMonthsSafe,
  adminLoginSchema,
  birthdaySchema,
  cancelSubscriptionSchema,
  consentStatusSchema,
  createSubscriptionSchema,
  daysBetween,
  emailSchema,
  formatCardMask,
  formatDate,
  formatDateShort,
  formatDateTime,
  formatDaysLeft,
  formatMoney,
  formatMonths,
  formatPhoneForDisplay,
  formatRuPhone,
  getUserMessage,
  isClientMissingError,
  isConfirmationCodeError,
  isCredentialsRejectedError,
  isRegistrationClosedError,
  isUserTokenError,
  isValidPhone,
  maskPhone,
  minorToRubles,
  minorToYookassaValue,
  nameSchema,
  normalizePhone,
  normalizeRuDigits,
  onlyDigits,
  optionalConsentSchema,
  optionalNameSchema,
  otpCodeSchema,
  parseRuDate,
  phoneSchema,
  plural,
  refundSchema,
  requestOtpSchema,
  requiredConsentSchema,
  rublesToMinor,
  toOnecDate,
  toOnecPhone,
  updateProfileSchema,
  verifyOtpSchema,
  yearsSince
};
//# sourceMappingURL=index.js.map